import React, { useState } from "react";
import { AlertTriangle, CalendarDays, ChevronDown, RefreshCw, Search, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useLanguage } from "@/i18n/LanguageContext";
import type { MonthOption } from "../types";

export const SelectField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  width: string;
  options: string[];
  placeholder?: string;
  searchable?: boolean;
}> = React.memo(({ label, value, onChange, width, options, placeholder, searchable }) => {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const emptyOption = placeholder ?? t("dash.form.all");

  if (searchable) {
    const filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase()));
    return (
      <div className="space-y-1.5">
        <span className="mb-0.5 flex items-center gap-1 text-[10px] font-medium text-slate-600">
          <Search className="h-3 w-3 text-primary-400" />
          {label}
        </span>
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("dash.form.searchIndicators")}
            className="input h-8 w-full pl-7 pr-6 text-xs"
          />
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        {value && (
          <div className="mb-1 flex items-center gap-1 rounded-lg bg-primary-100 px-2 py-1">
            <span className="flex-1 truncate text-[10px] font-medium text-primary-700">{value}</span>
            <button type="button" onClick={() => { onChange(""); setQuery(""); }} className="shrink-0 text-primary-400 hover:text-primary-600">
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="max-h-[120px] overflow-y-auto rounded-xl border border-slate-100 bg-white shadow-soft">
          {filtered.length === 0 ? (
            <div className="px-2 py-2 text-[10px] text-slate-400">{t("dash.form.noMatches")}</div>
          ) : (
            filtered.map((option) => {
              const active = value === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => { onChange(option); setQuery(""); }}
                  className={`block w-full truncate px-2 py-1.5 text-left text-[10px] transition hover:bg-primary-50 ${
                    active ? "bg-primary-100 font-medium text-primary-700" : "text-slate-600"
                  }`}
                >
                  {option}
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  }

  return (
    <label className="text-[10px] font-medium text-slate-600">
      <span className="mb-0.5 flex items-center gap-1 text-[10px]">
        <Search className="h-3 w-3 text-primary-400" />
        {label}
      </span>
      <span className={`relative block ${width}`}>
        <select
          className="input h-8 w-full appearance-none truncate py-0 pl-2 pr-6 text-xs"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option className="bg-white text-slate-400" value="">
            {emptyOption}
          </option>
          {options.map((option) => (
            <option className="bg-white text-slate-700" key={`${label}-${option}`} value={option}>
              {option}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
      </span>
    </label>
  );
});
SelectField.displayName = "SelectField";

export const YearMonthField: React.FC<{
  label: string;
  year: string;
  month: string;
  onYearChange: (value: string) => void;
  onMonthChange: (value: string) => void;
  years: string[];
  months: MonthOption[];
  listId: string;
}> = React.memo(({ label, year, month, onYearChange, onMonthChange, years, months, listId }) => {
  const { t } = useLanguage();
  return (
  <label className="block rounded-xl border border-primary-100 bg-primary-50/50 p-2 text-[10px] text-slate-600 shadow-sm">
    <span className="mb-1.5 flex items-center gap-1.5 font-semibold text-primary-700">
      <span className="flex h-5 w-5 items-center justify-center rounded-md bg-primary-100">
        <CalendarDays className="h-3 w-3 text-primary-600" />
      </span>
      {label}
      <span className="font-normal text-slate-400">{t("dash.form.ethiopianCalendar")}</span>
    </span>
    <div className="flex gap-1.5">
      <span className="relative">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[9px] font-medium text-slate-400">{t("dash.form.year")}</span>
        <input
          type="number"
          min={2010}
          max={2050}
          className="input h-8 w-24 border-primary-100 bg-white pl-9 pr-1 text-[10px] font-semibold text-slate-700 focus:border-primary-400"
          value={year}
          list={listId}
          onChange={(e) => onYearChange(e.target.value)}
        />
      </span>
      <span className="relative">
        <span className="pointer-events-none absolute left-2 top-1/2 z-10 -translate-y-1/2 text-[9px] font-medium text-slate-400">{t("dash.form.month")}</span>
        <select
          className="input h-8 w-36 border-primary-100 bg-white pl-11 text-[10px] font-semibold text-slate-700 focus:border-primary-400"
          value={month}
          onChange={(e) => onMonthChange(e.target.value)}
        >
          {months.map((option) => (
            <option key={`${listId}-${option.value}`} value={option.value}>
              {option.value} {option.label}
            </option>
          ))}
        </select>
      </span>
    </div>
    <datalist id={listId}>
      {years.map((option) => (
        <option key={`${listId}-${option}`} value={option} />
      ))}
    </datalist>
  </label>
  );
});
YearMonthField.displayName = "YearMonthField";

export const RunButton: React.FC<{ loading: boolean; onClick: () => void }> = React.memo(({ loading, onClick }) => {
  const { t } = useLanguage();
  return (
  <Button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="btn-primary h-8 px-4 text-xs"
  >
    <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
    {t("dash.run")}
  </Button>
  );
});
RunButton.displayName = "RunButton";

export const ErrorMessage: React.FC<{ message: string | null }> = React.memo(({ message }) =>
  message ? (
    <div className="mt-3 rounded-2xl border border-amber-200/60 bg-amber-50/80 px-4 py-2.5 text-xs text-amber-800">
      <div className="flex items-center gap-1.5 font-medium">
        <AlertTriangle className="h-3.5 w-3.5" />
        {message}
      </div>
    </div>
  ) : null);
ErrorMessage.displayName = "ErrorMessage";
